import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ColumnsContainer, DeviceSettingsButton, DollarSignIcon, getDeviceStatusText, PencilIcon, PopupContentContainer, ReportIcon, RowsContainer, SettingsIcon, ShareIcon, SingleRowButtonWithIcon, Toggle, } from '@streamr/tv-ui';
import { executeSoon } from '@streamr/tv-utils';
import cx from 'classnames';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { COMPLAINT_REPORT_URL } from '../../consts';
import { useShare } from '../../hooks/useShare';
import { BroadcastService } from '../../services/broadcast/BroadcastService';
import { MediaService } from '../../services/media/MediaService';
import { MultiBroadcasterService } from '../../services/multi-broadcaster/MultiBroadcasterService';
import { ScheduleService } from '../../services/schedule/ScheduleService';
import { Popup, TopRight } from '../../utils/popups';
import { useBroadcastUrl } from '../../utils/url';
import { Anchor } from '../Anchor';
import { AbstractPopup, DismissalReason, } from './AbstractPopup';
import { showBroadcastInfoEditPopup } from './BroadcastInfoEditPopup';
import { selectCamera } from './devices/CameraSelectorPopup';
import { selectMicrophone } from './devices/MicrophoneSelectorPopup';
import { ScrollablePopupContainer } from './helpers/ScrollablePopupContainer';
import { showAudienceContributionsSettingsPopup } from './merchant/AudienceContributionsSettingsPopup';
import { showNetworkStatisticsPopup } from './NetworkStatisticsPopup';
import { PopupCssPreset } from './Popup.styles';
export function BroadcastPopup({ reject, broadcast, onVideoQualityChanged, variant, context, ...props }) {
    const { canShare, openSystemShareDialog } = useShare();
    const scheduledBroadcast = useMemo(() => {
        if (!broadcast.streamId) {
            return undefined;
        }
        return ScheduleService.getInstance().getScheduledBroadcast(broadcast.streamId);
    }, [broadcast.streamId]);
    const selectedVideoDevice = MediaService.getOutput().useSelectedVideoDevice();
    const selectedAudioDevice = MediaService.getOutput().useSelectedAudioDevice();
    const isVideoDisabled = MediaService.getOutput().useVideoMuted();
    const isAudioDisabled = MediaService.getOutput().useAudioMuted();
    const videoPermissioned = MediaService.getOutput().useCameraPermissioned();
    const audioPermissioned = MediaService.getOutput().useMicrophonePermissioned();
    const noAudioDevices = MediaService.getOutput().useAudioDevices().length === 0;
    const noVideoDevices = MediaService.getOutput().useVideoDevices().length === 0;
    const broadcasterQuality = BroadcastService.getOutput().useQuality();
    const multiBroadcasterQuality = MultiBroadcasterService.getOutput().useBroadcastedVideoQuality();
    const quality = context === 'broadcaster'
        ? broadcasterQuality
        : multiBroadcasterQuality;
    // Quality now reflects service state; changes are notified to the service directly
    function isValidQuality(value) {
        return value === 'sd' || value === 'hd' || value === 'uhd';
    }
    const microphoneStatusText = useMemo(() => getDeviceStatusText(selectedAudioDevice, noAudioDevices, audioPermissioned), [selectedAudioDevice, noAudioDevices, audioPermissioned]);
    const cameraStatusText = useMemo(() => getDeviceStatusText(selectedVideoDevice, noVideoDevices, videoPermissioned), [selectedVideoDevice, noVideoDevices, videoPermissioned]);
    const microphoneButtonAnchorRef = useRef(null);
    const [microphoneSelectorOpen, setMicrophoneSelectorOpen] = useState(false);
    const cameraButtonAnchorRef = useRef(null);
    const [cameraSelectorOpen, setCameraSelectorOpen] = useState(false);
    const broadcastUrl = useBroadcastUrl(broadcast);
    return (_jsx(BroadcastPopupRoot, { reject: (reason) => {
            if (microphoneSelectorOpen || cameraSelectorOpen) {
                return;
            }
            reject(reason);
        }, translateFn: variant === 'desktop' ? TopRight : undefined, ...props, className: cx(props.className, variant), children: _jsx(ScrollablePopupContainer, { fullscreenOnly: variant === 'mobile', onCloseButtonClick: () => {
                reject(DismissalReason.CloseButton);
            }, children: _jsxs(BroadcastPopupContent, { children: [canShare && (_jsx(SingleRowButtonWithIcon, { label: "Share link", icon: _jsx(ShareIcon, {}), onClick: () => {
                            openSystemShareDialog(broadcastUrl);
                        } })), scheduledBroadcast && (_jsx(SingleRowButtonWithIcon, { label: "Edit broadcast", icon: _jsx(PencilIcon, {}), onClick: () => {
                            reject();
                            showBroadcastInfoEditPopup({
                                broadcast: scheduledBroadcast,
                                title: 'Edit broadcast',
                            });
                        } })), _jsx(SingleRowButtonWithIcon, { label: "Audience contributions", icon: _jsx(DollarSignIcon, {}), onClick: () => {
                            reject();
                            showAudienceContributionsSettingsPopup({
                                anchorRef: props.anchorRef,
                                variant,
                            });
                        } }), _jsxs(ColumnsContainer, { children: [_jsx(Anchor, { ref: microphoneButtonAnchorRef, children: _jsx(DeviceSettingsButton, { icon: "microphone", label: "Microphone", statusText: microphoneStatusText, isActive: !isAudioDisabled, displayErrorIcon: !audioPermissioned || noAudioDevices, onClick: () => {
                                        selectMicrophone({
                                            anchorRef: microphoneButtonAnchorRef,
                                            variant: 'bottom-center',
                                            onOpenPopup: () => {
                                                setMicrophoneSelectorOpen(true);
                                            },
                                            onClosePopup: () => {
                                                setMicrophoneSelectorOpen(false);
                                            },
                                        });
                                    } }) }), _jsx(Anchor, { ref: cameraButtonAnchorRef, children: _jsx(DeviceSettingsButton, { icon: "camera", label: "Camera", statusText: cameraStatusText, isActive: !isVideoDisabled, displayErrorIcon: !videoPermissioned || noVideoDevices, onClick: () => {
                                        selectCamera({
                                            anchorRef: cameraButtonAnchorRef,
                                            variant: 'bottom-center',
                                            onOpenPopup: () => {
                                                setCameraSelectorOpen(true);
                                            },
                                            onClosePopup: () => {
                                                setCameraSelectorOpen(false);
                                            },
                                        });
                                    } }) })] }), _jsx(PopupContentContainer, { children: _jsxs(RowsContainer, { children: [_jsx("span", { className: "tv-ui-font-medium", children: "Display quality" }), _jsx(Toggle, { options: ['sd', 'hd', 'uhd'], selected: quality, onChange: (newQuality) => {
                                        if (isValidQuality(newQuality)) {
                                            onVideoQualityChanged?.(newQuality);
                                        }
                                    } })] }) }), _jsx(SingleRowButtonWithIcon, { label: "Network graph", icon: _jsx(SettingsIcon, {}), onClick: () => {
                            reject();
                            showNetworkStatisticsPopup();
                        } }), _jsx(SingleRowButtonWithIcon, { label: "Report", icon: _jsx(ReportIcon, {}), onClick: () => {
                            reject();
                            window.open(COMPLAINT_REPORT_URL, '_blank');
                        } }), _jsxs(RecordingDisclaimer, { children: [_jsx("strong", { children: "CAUTION:" }), _jsx("br", {}), "Recording is a beta feature. ", _jsx("br", {}), "It's currently centralized and unencrypted."] })] }) }) }));
}
const BroadcastPopupContent = styled(RowsContainer) `
    width: 100%;
    gap: 1rem;
`;
const BroadcastPopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    &.desktop {
        @media (min-width: 30rem) {
            ${PopupCssPreset.AnchoredTopRight};
            --Popup_Width: 25rem;
            --Popup_Padding: 1rem 0;
        }
    }

    &.mobile {
        --SettingsContainer_InnerMaxWidth: 25rem;
    }
`;
const RecordingDisclaimer = styled.div `
    font-size: 0.875rem;
    color: var(--tv-ui-color-2);
    text-align: center;
    line-height: 1.25rem;

    strong {
        font-weight: 600;
    }
`;
const broadcastPopup = Popup.create(BroadcastPopup);
export function showBroadcastPopup({ anchorRef, variant, context, onBeforeOpen, onDismiss, broadcast, onVideoQualityChanged, }) {
    executeSoon(async () => {
        onBeforeOpen?.();
        try {
            await broadcastPopup.pop({
                anchorRef,
                variant,
                context,
                broadcast,
                onVideoQualityChanged,
            });
        }
        catch (_) {
        }
        finally {
            onDismiss?.();
        }
    });
}
